import { useLanguage } from "@/context/LanguageContext";
import { useNavigate, useLocation, Link } from "react-router-dom";

// Her dilin yasal sayfa rotaları
const legalRoutes = {
  tr: { privacy: "/gizlilik-politikasi", terms: "/kullanim-kosullari" },
  en: { privacy: "/privacy-policy", terms: "/terms-of-service" },
  ar: { privacy: "/ar/gizlilik-politikasi", terms: "/ar/kullanim-kosullari" },
};

export function Footer() {
  const { t, lang } = useLanguage();
  const year = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  // Mevcut dilin ana sayfa path'i
  const langHomeMap = { tr: "/", en: "/en", ar: "/ar" };
  const homePath = langHomeMap[lang] || "/";
  const legal = legalRoutes[lang] || legalRoutes.tr;

  const scrollTo = (href) => {
    const isOnHome = location.pathname === homePath;
    if (isOnHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(homePath);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img
              src="/images/entegrelogo/EP-Logo-Normal.svg"
              alt={t("imageAlt.logo")}
              width="160"
              height="32"
              className="h-8 brightness-0 invert mb-4"
              loading="lazy"
            />
            <p className="text-sm text-slate-400 max-w-md leading-relaxed">
              {t("footer.description")}
            </p>
            <div className="flex gap-3 mt-6">
              <a
                href="https://wa.me/+905331631634"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-green-600 transition-colors"
                data-testid="footer-whatsapp"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">{t("footer.quickLinks")}</h4>
            <ul className="space-y-2.5">
              {[
                { label: t("nav.features"), href: "#features" },
                { label: t("nav.pricing"), href: "#pricing" },
                { label: t("nav.faq"), href: "#faq" },
                { label: t("nav.contact"), href: "#contact" },
              ].map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => scrollTo(link.href)}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">{t("footer.legal")}</h4>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to={legal.privacy}
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                  data-testid="footer-privacy-link"
                >
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={legal.terms}
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                  data-testid="footer-terms-link"
                >
                  {t("footer.terms")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <hr className="my-10 border-slate-800" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            &copy; {year} EntegrePanel. {t("footer.rights")}
          </p>
          <p className="text-xs text-slate-500">
            Hobala E-Ticaret Reklam Ve Aracılık LTD ŞTİ | UR-GE Yazılım
          </p>
        </div>
      </div>
    </footer>
  );
}
